import React, { useState } from "react";
import { ChevronDownIcon, LayoutDashboardIcon, ListOrderedIcon, HomeIcon, RadarIcon, RouteIcon, ClipboardCheckIcon, GemIcon, FileTextIcon } from "lucide-react";
import { BRAND } from "../lib/data";
export type DemoView = "home" | "orders" | "tracker" | "report" | "ops-board" | "ops-detail" | "ops-analytics" | "recruiting";
type DemoGroup = "Customer" | "Admin" | "Recruiting";
type DemoNavProps = {
  active: DemoView;
  onNavigate: (view: DemoView) => void;
};
const CUSTOMER_VIEWS: DemoView[] = ["home", "orders", "tracker", "report"];
const RECRUITING_VIEWS: DemoView[] = ["recruiting"];

// Recruiting persona accent (distinct from burgundy customer & lime admin)
const RECRUIT_COLOR = "#B08114";
const labels: Record<DemoView, {
  label: string;
  group: DemoGroup;
  Icon: typeof HomeIcon;
}> = {
  home: {
    label: "Agent Home",
    group: "Customer",
    Icon: HomeIcon
  },
  orders: {
    label: "My Orders",
    group: "Customer",
    Icon: ListOrderedIcon
  },
  tracker: {
    label: "Order Tracker",
    group: "Customer",
    Icon: RadarIcon
  },
  report: {
    label: "Digital Ads Report",
    group: "Customer",
    Icon: FileTextIcon
  },
  "ops-board": {
    label: "Order Routing",
    group: "Admin",
    Icon: RouteIcon
  },
  "ops-detail": {
    label: "Order Detail",
    group: "Admin",
    Icon: ClipboardCheckIcon
  },
  "ops-analytics": {
    label: "Operations Analytics",
    group: "Admin",
    Icon: LayoutDashboardIcon
  },
  recruiting: {
    label: "Exclusive Benefits",
    group: "Recruiting",
    Icon: GemIcon
  }
};
export function DemoNav({
  active,
  onNavigate
}: DemoNavProps) {
  const [open, setOpen] = useState(false);
  const item = labels[active];
  const isCustomer = CUSTOMER_VIEWS.includes(active);
  const isRecruiting = RECRUITING_VIEWS.includes(active);
  const cornerStyle = isCustomer ? {
    borderColor: `${BRAND.accent}55`,
    color: BRAND.accentText,
    dot: BRAND.accent
  } : isRecruiting ? {
    borderColor: `${RECRUIT_COLOR}66`,
    color: RECRUIT_COLOR,
    dot: RECRUIT_COLOR
  } : {
    borderColor: "#9FE000",
    color: "#1A2E05",
    dot: "#C6FF00"
  };
  const cornerPrefix = isCustomer ? "Customer" : isRecruiting ? "Recruiting" : "Admin Demo";

  // The report is a standalone, full-width document with its own letterhead, so
  // the floating persona tab would overlap its content — hide it there.
  const showCornerTab = active !== "report";
  return <>
      {showCornerTab && <aside className="fixed right-4 top-[4.6rem] z-40 hidden sm:block" data-id="element-255" data-mp="val12ith7zao">
          <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.09em] shadow-lg shadow-neutral-900/10" style={{
        borderColor: cornerStyle.borderColor,
        color: cornerStyle.color
      }} data-id="element-256" data-mp="1s1n7j55da0io">
            <span className="h-2 w-2 rounded-full" style={{
          backgroundColor: cornerStyle.dot
        }} data-id="element-257" data-mp="1e2460v729nfc" />
            {`${cornerPrefix} · ${item.label}`}
          </div>
        </aside>}

      <div className="fixed bottom-4 left-4 z-50" data-id="element-258" data-mp="2nqu8opl0yfo">
        {open && <div className="mb-2 w-64 overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900 p-2 shadow-2xl shadow-neutral-900/30" data-id="element-259" data-mp="3ophinohzk5hp">
            <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500" data-id="element-260" data-mp="3tpc6fj6gseyj">Customer journey</p>
            {(["home", "orders", "tracker", "report"] as DemoView[]).map(view => <DemoNavItem key={view} view={view} active={active} onNavigate={onNavigate} onClose={() => setOpen(false)} data-id="element-261" data-mp="79rffdkts588" />)}
            <div className="mx-2 my-2 h-px bg-neutral-800" data-id="element-262" data-mp="baba8ahxewr7" />
            <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#C6FF00]" data-id="element-263" data-mp="g8mn6u3ipndn">Internal admin</p>
            {(["ops-board", "ops-analytics"] as DemoView[]).map(view => <DemoNavItem key={view} view={view} active={active} onNavigate={onNavigate} onClose={() => setOpen(false)} data-id="element-264" data-mp="q8dj907q8afu" />)}
            <div className="mx-2 my-2 h-px bg-neutral-800" data-id="element-265" data-mp="2n15kffv8hgip" />
            <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em]" style={{
          color: RECRUIT_COLOR
        }} data-id="element-266" data-mp="2rz6zlppjgvow">Recruiting</p>
            {(["recruiting"] as DemoView[]).map(view => <DemoNavItem key={view} view={view} active={active} onNavigate={onNavigate} onClose={() => setOpen(false)} data-id="element-267" data-mp="mcr9id9y0dni" />)}
          </div>}
        <button type="button" onClick={() => setOpen(value => !value)} className="flex items-center gap-2 rounded-full bg-neutral-900 px-3 py-2.5 text-xs font-semibold text-white shadow-xl shadow-neutral-900/25 transition hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-[#C6FF00] focus:ring-offset-2" aria-expanded={open} aria-label="Open demo page switcher" data-id="element-268" data-mp="vbuq9aln0gyu">
          <span className="rounded-full bg-[#C6FF00] px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#1A2E05]" data-id="element-269" data-mp="ce1quia82p67">Demo</span>
          <span className="hidden sm:inline" data-id="element-270" data-mp="3g1bs7vq7wxgc">View switcher</span>
          <ChevronDownIcon className={`h-3.5 w-3.5 transition ${open ? "rotate-180" : ""}`} data-id="element-271" data-mp="3fzr16g343wn8" />
        </button>
      </div>
    </>;
}
function DemoNavItem({
  view,
  active,
  onNavigate,
  onClose
}: {
  view: DemoView;
  active: DemoView;
  onNavigate: (view: DemoView) => void;
  onClose: () => void;
}) {
  const {
    label,
    Icon,
    group
  } = labels[view];
  const selected = view === active;
  return <button type="button" onClick={() => {
    onNavigate(view);
    onClose();
  }} className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition ${selected ? "bg-white/12 text-white" : "text-neutral-300 hover:bg-white/8 hover:text-white"}`} data-id="element-272" data-mp="1hluruczndgrj">
      <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${group === "Admin" ? "bg-[#C6FF00] text-[#1A2E05]" : "text-white"}`} style={group === "Customer" ? {
      backgroundColor: BRAND.accent
    } : group === "Recruiting" ? {
      backgroundColor: RECRUIT_COLOR
    } : undefined} data-id="element-273" data-mp="2e9hn47kzit3e">
        <Icon className="h-3.5 w-3.5" data-id="element-274" data-mp="oxfia5zabqnl" />
      </span>
      <span className="flex-1 font-medium" data-id="element-275" data-mp="tupec4h6j0ke">{label}</span>
      {selected && <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-400" data-id="element-276" data-mp="9sbc6t5ehwqm">Here</span>}
    </button>;
}